import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from '../authUtils';
import {
  Box, Button, Typography, CircularProgress, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, Chip, TablePagination,
  IconButton, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import urlconfig from '../config/urlconfig';

const MissingCatalogDashboard = () => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination and Data State
  const [missingData, setMissingData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  // State to manage copy tooltip feedback
  const [copiedId, setCopiedId] = useState(null);
  const loggedInUser = accounts && accounts.length > 0 ? accounts[0].name : "";

  const fetchMissingCatalogData = async () => {
    setLoading(true);
    setError(null);
    try {
      const account = accounts[0];
      const accessToken = await getApiToken(instance, account);

      const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/admin/missing-catalog-info?createdBy=${encodeURIComponent(loggedInUser)}&page=${page}&size=${rowsPerPage}`,{
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error('Failed to fetch missing catalog info.');

      const data = await response.json();
      setMissingData(data.content);
      setTotalElements(data.totalElements);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMissingCatalogData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to copy SO Number
  const handleCopySO = (e, soNumber, itemId) => {
    e.stopPropagation(); // Prevents the accordion from toggling when clicking the button
    navigator.clipboard.writeText(soNumber);
    setCopiedId(itemId);

    // Reset tooltip after 2 seconds
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body1">
          Found <strong>{totalElements}</strong> orders containing items with missing catalog data.
        </Typography>
        <Button
          variant="outlined"
          onClick={fetchMissingCatalogData}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Refresh Data
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {missingData.map((item) => (
        <Accordion key={item.itemId} sx={{ mb: 1, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>

              {/* Updated Header Section with Copy Button */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 1 }}>
                  SO No: {item.salesOrderNo}
                </Typography>

                {item.salesOrderNo !== 'N/A' && (
                  <Tooltip title={copiedId === item.itemId ? "Copied!" : "Copy SO Number"}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleCopySO(e, item.salesOrderNo, item.itemId)}
                      sx={{ mr: 2, padding: '2px' }}
                    >
                      <ContentCopyIcon fontSize="small" color={copiedId === item.itemId ? "success" : "action"} />
                    </IconButton>
                  </Tooltip>
                )}


                <Typography variant="subtitle1" fontWeight="bold">
                Created By: {item.createdBy}  | Sales SPOC: {item.salesPOC} | SCM SPOC: {item.scmPOC}
                </Typography>
              </Box>

              <Chip
                label={`${item.missingCount} Sub-Items Missing Data`}
                color="warning"
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>Sub-Item Details</strong></TableCell>
                    <TableCell><strong>ESIN Status</strong></TableCell>
                    <TableCell><strong>Product Name Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {item.missingSubItems.map((sub) => (
                    <TableRow key={sub.subItemId}>
                      <TableCell>{sub.details}</TableCell>
                      <TableCell>
                        <span style={{ color: sub.esinStatus === 'Missing' ? 'red' : 'green', fontWeight: 'bold' }}>
                          {sub.esinStatus}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: sub.ecomProductNameStatus === 'Missing' ? 'red' : 'green', fontWeight: 'bold' }}>
                          {sub.ecomProductNameStatus}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}

      {totalElements > 0 && (
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}

      {!loading && missingData.length === 0 && !error && (
        <Alert severity="success">All items currently have ESIN and Ecom Product Names filled out!</Alert>
      )}
    </Box>
  );
};

export default MissingCatalogDashboard;